import type {CreateExtArgs} from '@wix/document-manager-core'
import type {
    Callback,
    Callback1,
    CompRef,
    IdMap,
    IdMapValue,
    ImageGenerationServerlessResponseWithIdMap,
    ImageGenerationServerlessResponseWithIdMapAndOldUrlMap,
    ImageInfo,
    ImagesInfoMap,
    UserRequestInfo
} from '@wix/document-services-types'
import {reportBIError} from '../aiExtensionContent'
import {ERRORS, LOGS} from '../constants'
import {ReportableError} from '@wix/document-manager-utils'
import {handleAiError} from '../aiBuilderUtils'
import {createMediaMap} from '../utils'
import type {DataExtensionAPI} from '../../data'
import {isEmpty, mapValues} from 'lodash'
import type {ImagesRequestBody} from './requests'
import {STATIC_MEDIA_URL} from '@wix/image-kit'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import {createUploadContext, type UploadConfig, uploadFilesWithHandlers} from '../fileUploadHandler'
import type {LoggerExtAPI} from '../../logger'

interface ValidationResult {
    isValid: boolean
    reason: string
}

interface ImageGenerationParams {
    userRequestInfo?: UserRequestInfo
    onSuccess?: Callback
    onError?: Callback1<any>
    caller: string
    errorType: string
}

const injectImage = (createExtArgs: CreateExtArgs, idMapValue: IdMapValue, imageInfo: ImageInfo) => {
    const {dataModel} = createExtArgs.extensionAPI as DataModelExtensionAPI
    const {data} = createExtArgs.extensionAPI as DataExtensionAPI
    const {pointer, prop, path, nestedDataPath} = idMapValue ?? {}
    const {imageUri, width, height, name} = imageInfo
    if (!pointer) return
    const imageData = dataModel.createDataItemByType('Builder.Image', {
        uri: imageUri,
        ...(width && height ? {width, height} : {}),
        ...(name ? {name} : {})
    })
    const dataToUpdate = {[prop]: imageData}
    data.injectData({pointer, path, nestedDataPath, partialNewData: dataToUpdate})
}

const imageUploadConfig: UploadConfig<ImageInfo> = {
    extractData: (output: any) => output?.file?.media?.image?.image,
    transformData: (imageData: any): ImageInfo => ({
        imageUri: imageData.id,
        width: imageData.width,
        height: imageData.height,
        name: imageData.filename
    }),
    injectItem: injectImage,
    errorMessage: 'Image upload failed',
    timeOutInMS: 60000
}

export const validateResult = (images: ImagesInfoMap, idMap: IdMap): ValidationResult => {
    const missingImagesUrl = Object.keys(idMap).filter(img => !images.hasOwnProperty(img))
    return {
        isValid: missingImagesUrl.length === 0,
        reason:
            missingImagesUrl.length === 0
                ? ''
                : `Failed to generate ${
                      Object.keys(idMap).length - Object.keys(images).length
                  } urls for ${missingImagesUrl}`
    }
}

export const uploadImages = async (
    createExtArgs: CreateExtArgs,
    imagesInfo: ImagesInfoMap,
    idMap: IdMap,
    oldImagesInfo: ImagesInfoMap,
    failures: string[] = [],
    onSuccess?: Callback1<any>,
    onError?: Callback1<any>
) => {
    const context = createUploadContext({
        createExtArgs,
        idMap,
        originalData: oldImagesInfo,
        onSuccess,
        onError,
        failures,
        totalItems: Object.keys(imagesInfo).length
    })

    const items = mapValues(imagesInfo, ({imageUri}) => imageUri)
    await uploadFilesWithHandlers(context, imageUploadConfig, items)
}

export const injectImages = (
    createExtArgs: CreateExtArgs,
    {data: imagesInfo, idMap}: ImageGenerationServerlessResponseWithIdMap
) => {
    Object.entries(imagesInfo).forEach(([id, {imageUri, width, height}]) => {
        injectImage(createExtArgs, idMap[id], {imageUri, width, height})
    })
    createExtArgs.dal.commitTransaction()
}

export const uploadAndInjectImages = async (
    createExtArgs: CreateExtArgs,
    {data: imagesInfo, idMap, oldImagesInfo}: ImageGenerationServerlessResponseWithIdMapAndOldUrlMap,
    failures: string[] = [],
    onSuccess?: Callback1<any>,
    onError?: Callback1<any>,
    caller?: string
): Promise<void> => {
    injectImages(createExtArgs, {data: imagesInfo, idMap})

    if (!caller) {
        caller = 'uploadAndInjectImages'
    }
    const {logger} = createExtArgs.extensionAPI as LoggerExtAPI
    logger.interactionStarted(LOGS.IMAGES.UPLOAD, {
        tags: {caller},
        extras: {imagesAmount: Object.keys(imagesInfo).length}
    })
    const onSuccessWithLog = (arg: any) => {
        logger.interactionEnded(LOGS.IMAGES.UPLOAD, {tags: {caller}})
        logger.interactionEnded(caller, {tags: {caller}})
        onSuccess?.(arg)
    }
    const onErrorWithLog = (error: any) => {
        const reportableError = new ReportableError({
            errorType: ERRORS.IMAGES.UPLOAD,
            message: 'Images upload failed',
            extras: {
                originalError: error
            }
        })
        logger.captureError(reportableError, {tags: {caller}})
        onError?.(error)
    }

    await uploadImages(createExtArgs, imagesInfo, idMap, oldImagesInfo, failures, onSuccessWithLog, onErrorWithLog)
}

export const processGeneratedImages = async (
    createExtArgs: CreateExtArgs,
    imagesInfo: ImagesInfoMap,
    idMap: IdMap,
    oldImagesInfo: ImagesInfoMap,
    onSuccess: Callback1<any> | undefined,
    onError: Callback1<any> | undefined,
    caller: string
) => {
    const {logger} = createExtArgs.coreConfig
    const validation = validateResult(imagesInfo, idMap)
    const failures: string[] = []

    if (!validation.isValid) {
        reportBIError(
            logger,
            ERRORS.IMAGES.GENERATION_VALIDATION_ERROR,
            validation.reason,
            imagesInfo,
            validation,
            caller
        )

        if (isEmpty(imagesInfo)) {
            const reportableError = new ReportableError({
                errorType: ERRORS.IMAGES.GENERATION,
                message: 'Images generation returned an empty object'
            })
            logger.captureError(reportableError, {tags: {caller}})
            onError?.({errors: ['No images generated']})
        }
        failures.push(validation.reason)
    }

    await uploadAndInjectImages(
        createExtArgs,
        {data: imagesInfo, idMap, oldImagesInfo},
        failures,
        onSuccess,
        onError,
        caller
    )
}

export const createStaticUrl = (uri: string, extras?: Record<string, any>) => {
    if (uri.startsWith('http')) {
        throw new ReportableError({
            errorType: 'ImageUriError',
            message: 'Image uri should be image id and not a full url',
            extras
        })
    }
    return STATIC_MEDIA_URL + uri
}

export const generateAndInjectImages = async <TParams>(
    createExtArgs: CreateExtArgs,
    serverFacade: any,
    componentPointers: CompRef[],
    buildParams: (key: string, url: string, width: number, height: number) => TParams,
    fetcher: (serverFacade: any, msid: string, requestBody: ImagesRequestBody<TParams>) => Promise<ImagesInfoMap>,
    {userRequestInfo, onSuccess, onError, caller, errorType}: ImageGenerationParams
): Promise<void> => {
    const {extensionAPI} = createExtArgs
    const {logger} = extensionAPI as LoggerExtAPI
    const msid = createExtArgs.dal.get(createExtArgs.pointers.rendererModel.getMetaSiteId())

    logger.interactionStarted(caller, {tags: {caller}})

    const {idMap, originalData, mediaMeasurements} = createMediaMap(extensionAPI, componentPointers, 'image')
    const oldImageInfoMap: ImagesInfoMap = mapValues(idMap, (idMapValue, mappedId) => ({
        imageUri: originalData[mappedId]?.uri,
        width: originalData[mappedId]?.width,
        height: originalData[mappedId]?.height
    }))

    const images: Record<string, TParams> = mapValues(oldImageInfoMap, ({imageUri}, key) => {
        const {width, height} = mediaMeasurements[key]
        return buildParams(key, createStaticUrl(imageUri, {componentPointer: idMap[key].pointer}), width, height)
    })

    const requestBody = {images, userRequestInfo}

    let imagesInfo: ImagesInfoMap
    try {
        logger.interactionStarted(LOGS.IMAGES.GENERATION, {tags: {caller}})
        imagesInfo = await fetcher(serverFacade, msid, requestBody)
        logger.interactionEnded(LOGS.IMAGES.GENERATION, {tags: {caller}})

        await processGeneratedImages(createExtArgs, imagesInfo, idMap, oldImageInfoMap, onSuccess, onError, caller)
    } catch (error: any) {
        handleAiError(error, errorType, logger, onError, {caller})
    }
}
